import * as React from 'react';
import { observer } from 'mobx-react';

import { TicketStatusType, StakeType } from 'eTip--common';

import { CountDownLoader, ToggleSwitch, AccountTypes, IAccountTypesProps, SavedTicket, AcceptOrReject } from 'common--design-library';
import { TicketErrors } from 'desktop--design-library';

import { translationForMts } from 'easit--localization';
import classNames from 'classnames';

export interface ITicketProps extends IAccountTypesProps {

    isSystem: boolean;
    ticketStakeType: StakeType;
    isAcceptChangedRatesAndEvalInProgress: boolean;
    isTicketCopyInProgress: boolean;
    isSystemTicketUpdating: boolean;
    areRatesUpToDate: boolean;
    ticketStatus: TicketStatusType;
    errors?: string[];
    errorsAboveSubmitButton?: string[];
    isEnabledUI: boolean;
    tipsCount: number;

    tips: React.ReactNode;
    summary: React.ReactNode;
    cardinalityNode?: React.ReactNode;
    toggleSystemColor: string;
    amlOptions?: React.ReactNode;

    canPrepareTicket?: boolean;
    savedTicketNumber?: string;
    savedTicketExpiration?: Date;
    isCountDownEnabled: boolean;
    numberToCountDown: number;
    canShowProductsInTicket: boolean;

    roleForTest?: string;

    changeTicketStakeType: (stakeType: StakeType) => void;
    toggleSystem: (value: boolean) => void;
    clearTicket: () => void;
    ticketPay: () => void;
    ticketEvaluate: () => void;
    prepareJustSentTicketCopy: () => void;
    prepareTicket: () => void;
}

export const Ticket: React.FC<ITicketProps> = observer((props: ITicketProps) => {

    const t = translationForMts();

    return <div className="ticket" data-test-role={props.roleForTest}>
        {/*
            props.isSystemTicketUpdating &&
            <div className="loading-cover"><span className="fas fa-spinner fa-spin"></span> {translate("coupon_UpdatingSystem")}</div>
            */
        }

        {
            props.ticketStatus === TicketStatusType.New &&
            props.amlOptions
        }

        <div className="combi-switch-wrapper">
            {t.ticket_SelectionCount}: {props.tipsCount}
            <span className="ml-auto mr-2">{t.coupon_SystemSwitchLabel}</span>
            <ToggleSwitch
                onToggle={(v) => { props.isEnabledUI && props.toggleSystem(v); }}
                value={props.isSystem}
                selectedColor={props.toggleSystemColor}
                roleForTest="combi-switch"
            />
        </div>

        {
            (props.ticketStatus === TicketStatusType.New || props.ticketStatus === TicketStatusType.Pay) &&
            <div>
                <div className="tips" data-test-role="tips">
                    {props.tips}
                </div>

                {
                    <TicketErrors errors={props.errors} />
                }

                {
                    props.isSystem &&
                    <div className="system-coupon-wrapper">
                        {props.cardinalityNode}
                        <div className="system-coupon-type">
                            <div className={classNames("action-button", __IsMTSTipos__ ? "btn" : null, props.ticketStakeType === StakeType.All ? 'selected' : '')}
                                onClick={() => { props.isEnabledUI && props.changeTicketStakeType(StakeType.All); }}
                                data-test-role="system-coupon-types__item" data-id="all"
                            >{t.ticket_StakeTypeAll}</div>
                            <div className={classNames("action-button", __IsMTSTipos__ ? "btn" : null, props.ticketStakeType === StakeType.Total ? 'selected' : '')}
                                onClick={() => { props.isEnabledUI && props.changeTicketStakeType(StakeType.Total); }}
                                data-test-role="system-coupon-types__item" data-id="total"
                            >{t.ticket_StakeTypeTotal}</div>
                            <div className={classNames("action-button", __IsMTSTipos__ ? "btn" : null, props.ticketStakeType === StakeType.Unit ? 'selected' : '')}
                                onClick={() => { props.isEnabledUI && props.changeTicketStakeType(StakeType.Unit); }}
                                data-test-role="system-coupon-types__item" data-id="unit"
                            >{t.ticket_StakeTypeUnit}</div>
                        </div>
                    </div>
                }

                {
                    (!props.isSystem && props.canShowProductsInTicket) &&
                    <AccountTypes
                        availableAccountTypes={props.availableAccountTypes}
                        selectedAccountType={props.selectedAccountType}
                        setAcountType={props.setAcountType}
                        getFreeTicketBonusUrl={props.getFreeTicketBonusUrl}
                    />

                }

                {props.summary}

                <TicketErrors errors={props.errorsAboveSubmitButton} />

                <div className="pay-buttons">
                    <button className={classNames("btn", __IsMTSTipos__ ? "btn-primary" : "btn-secondary")} onClick={() => { props.isEnabledUI && props.ticketEvaluate() }}>
                        {props.areRatesUpToDate || props.errors && props.errors.length > 0 ? t.coupon_evaluateBtn : t.coupon_acceptRateChangeBtn /* same in ticketStandardAndLive and ticket.tsx */} {props.isAcceptChangedRatesAndEvalInProgress ? <span className="fas fa-spinner fa-spin"></span> : ''}
                    </button>
                    {
                        props.canPrepareTicket
                            ? <button className=" btn btn-primary" disabled={!props.areRatesUpToDate /* same in ticketStandardAndLive and ticket.tsx */} onClick={() => { if (props.isEnabledUI) { props.prepareTicket(); } }} >{t.ticket_PrepareTicket}</button>
                            : <button className={classNames("btn", __IsMTSTipos__ ? "btn-success" : "btn-primary")} disabled={!props.areRatesUpToDate /* same in ticketStandardAndLive and ticket.tsx */} onClick={() => { if (props.isEnabledUI && props.areRatesUpToDate /* same in ticketStandardAndLive and ticket.tsx */) { props.ticketPay(); } }} data-test-role="ticket-submit" >
                                {t.coupon_sendToPay}
                                {
                                    (props.ticketStatus === TicketStatusType.Pay) &&
                                    <>
                                        <span className="fas fa-spinner fa-spin ml-2"></span>
                                        {
                                            props.isCountDownEnabled &&
                                            <span className="ml-2"> <CountDownLoader cnt={props.numberToCountDown} /></span>
                                        }
                                    </>
                                }

                            </button>
                    }
                </div>
            </div>
        }

        {
            (props.ticketStatus === TicketStatusType.Accepted || props.ticketStatus === TicketStatusType.Rejected) &&
            <AcceptOrReject
                clearCoupon={props.clearTicket}
                prepareJustSentCouponCopy={props.prepareJustSentTicketCopy}
                isAccepted={props.ticketStatus === TicketStatusType.Accepted}
                allowTicketCopy={!props.isSystem}
                isTicketCopyInProgress={props.isTicketCopyInProgress}
                canSwapPrimarySecondary={false}
            />
        }
        {
            props.ticketStatus === TicketStatusType.Saved &&
            <SavedTicket
                ticketNumber={props.savedTicketNumber}
                expirationDate={props.savedTicketExpiration}
                onCreateIdenticalTicket={props.prepareJustSentTicketCopy}
                clearCoupon={props.clearTicket}
            />

        }
    </div>
});